import { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Heading,
  IconButton,
  useDisclosure,
  Button,
  Text,
  Link,
  Input,
} from "@chakra-ui/react";
import { crm } from "../../theme";
import TableWithPagination from "../../components/TableWithPagination";
import { useCompany } from "../../contexts/CompanyContext";
import { useMediaQuery } from "react-responsive";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import {
  downloadFile,
  getDocuments,
} from "../../services/documentService";
import Filter from "../../components/Filter";
import { Tag } from "../../models/Filter";
import { DashboardQuery } from "../../models/Dashboard";
import { useNavigate, useLocation } from "react-router-dom";
import { FaAngleRight } from "react-icons/fa";
import FilterModal from "../../components/FilterModal";
import Layout from "../../components/Layout";

const Documents = () => {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const [loadingFile, setLoadingFile] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<DashboardQuery>();
  const { currentCompany, currentEmulation } = useCompany();
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [tags, setTags] = useState<Tag[]>([]);
  const [page, setPage] = useState<any[]>([]);
  const [filteredPage, setFilteredPage] = useState<any[]>([]);
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [initialDate, setInitialDate] = useState<string>();
  const [finalDate, setFinalDate] = useState<string>();

  const download = async (p: any) => {
    setLoadingFile(true);
    var file = await downloadFile(p.path);
    const linkSource = `data:application/pdf;base64,${file}`;
    const downloadLink = document.createElement("a");
    downloadLink.href = linkSource;
    downloadLink.download = `${p.cvfRef} - ${p.name}`;
    downloadLink.click();
    setLoadingFile(false);
  };

  const location = useLocation();
  const id = new URLSearchParams(location.search).get("id");

  const get = async (
    localPageSize?: number,
    localPageNumber?: number,
    sortField?: string,
    sortDirection?: string
  ) => {
    if (!currentCompany) return { data: [] };

    setLoading(true);
    const localPs = localPageSize ?? pageSize;
    const localPn = localPageNumber ?? pageIndex;

    const response = await getDocuments({
      processId: id ?? undefined,
      pageSize: id ? 10000 : localPs,
      pageNumber: localPn,
      sortField,
      type: currentCompany,
      sortDirection,      
      initialDate: initialDate,
      finalDate: finalDate,
      ncm: tags
        .filter((t) => t.type === "ncm")
        ?.map((t) => t.value)
        .join("|"),
      departurePort: tags
        .filter((t) => t.type === "departurePort")
        ?.map((t) => t.value)
        .join("|"),
      cvfRef: tags
        .filter((t) => t.type === "cvfRef")
        ?.map((t) => t.value)
        .join("|"),
      dischargePort: tags
        .filter((t) => t.type === "dischargePort")
        ?.map((t) => t.value)
        .join("|"),
      companyId: currentEmulation,
      countryOfOrigin: tags
        .filter((t) => t.type === "origin")
        ?.map((t) => t.value)
        .join("|"),
      exporter: tags
        .filter((t) => t.type === "supplier")
        ?.map((t) => t.value)
        .join("|"),
      name: tags
        .filter((t) => t.type === "name")
        ?.map((t) => t.value)
        .join("|"),
    });
    setPage(response.data);
    setFilteredPage(response.data);
    setLoading(false);
    return response;
  };
  useEffect(() => {
    get();
  }, [tags, currentCompany, currentEmulation, id]);

  useEffect(() => {
    if (initialDate && finalDate) get();
  }, [initialDate, finalDate]);

  return (
    <Box height="100%">
      <Layout>
        <Flex
          alignItems="center"
          flexDirection="column"
          backgroundColor="var(--main-bg-color)"
        >
          {id && (
            <Flex
              alignItems="center"
              gap="8px"
              width="90%"
              mt={{ base: "16px", md: "27px" }}
            >
              <Link
                color="var(--icon-color)"
                onClick={() => navigate("/processes")}
                size="md"
              >
                Processos
              </Link>
              <FaAngleRight />
              <Link
                color="var(--icon-color)"
                onClick={() => navigate(`/process?id=${id}`)}
                size="md"
              >
                Detalhes
              </Link>
              <FaAngleRight />
              <Text size="md">Documentos</Text>
            </Flex>
          )}
          <Flex width="90%" mt="48px" mb="24px" align="center" gap="12px">
            <Box w="4px" h="34px" bg={crm.brand} borderRadius="full" />
            <Box>
              <Text fontSize="28px" fontWeight="700" color={crm.text} lineHeight="1.1">Documentos{id && page[0] ? ` do processo ${page[0].cvfRef}` : ""}</Text>
              <Text color={crm.textMuted} fontSize="sm" mt="2px">Acesse e baixe os documentos dos processos.</Text>
            </Box>
          </Flex>
          <Flex
            backgroundColor={crm.surface}
            border={`1px solid ${crm.border}`}
            boxShadow={crm.shadowSm}
            borderRadius="12px"
            width="90%"
            paddingBottom="32px"
            mb="48px"
          >
            <FilterModal
              types={[
                { label: "Nº do processo", type: "cvfRef" },
                { label: "Pesquisar por nome do arquivo", type: "name" },
              ]}
              {...{
                isOpen,
                onClose,
                tags,
                setTags,
              }}
            />
            {isMobile ? (
              <Flex w="100%" justifyContent="center" mt="32px">
                <Button
                  w="80%"
                  backgroundColor="var(--icon-color)"
                  color="white"
                  onClick={onOpen}
                >
                  Filtrar documentos
                </Button>
              </Flex>
            ) : (
              <Box pl="32px" pt="32px" borderRadius="8px">
                <Filter
                  extraFilters={[
                    ,
                    <Box>
                      <Heading size="sm" mb="8px">
                        Início do período
                      </Heading>
                      <Input
                        backgroundColor={"white"}
                        type="date"
                        width={isMobile ? "80vw" : "16vw"}
                        value={initialDate}
                        onChange={(e) => setInitialDate(e.target.value)}
                      />
                    </Box>,
                    <Box>
                      <Heading size="sm" mb="8px">
                        Fim do período
                      </Heading>
                      <Input
                        backgroundColor={"white"}
                        type="date"
                        width={isMobile ? "80vw" : "16vw"}
                        value={finalDate}
                        onChange={(e) => setFinalDate(e.target.value)}
                      />
                    </Box>,
                  ]}
                  types={[
                    { label: "Nº do processo", type: "cvfRef" },
                    { label: "Pesquisar por nome do arquivo", type: "name" },
                  ]}
                  {...{
                    tags,
                    setTags,
                  }}
                />
              </Box>
            )}
          </Flex>
          <Flex
            width={isMobile ? "95%" : "90%"}
            height="100%"
            backgroundColor={crm.surface}
            borderRadius="12px"
            border={`1px solid ${crm.border}`}
            boxShadow={crm.shadowSm}
            overflow="hidden"
            mb="32px"
            flexDirection="column"
          >
            <Box p={isMobile ? 0 : "32px"} pt="32px">
              <TableWithPagination
                loading={loading}
                columns={
                  id || isMobile
                    ? [
                        {
                          label: "Nome do documento",
                          data: (p: any) => (
                            <Text
                              overflow="hidden"
                              maxWidth={isMobile ? "30vh" : undefined}
                              size="md"
                            >
                              {isMobile ? `${p.cvfRef} - ` : ""} {p.name}
                            </Text>
                          ),
                        },
                        {
                          label: "",
                          data: (p: any) => (
                            <IconButton
                              onClick={() => download(p)}
                              aria-label="Download"
                              variant="ghost"
                              color={crm.brand}
                              _hover={{ bg: crm.brandSoft }}
                              icon={<FontAwesomeIcon icon={faDownload} />}
                            />
                          ),
                        },
                      ]
                    : [
                        {
                          label: "Nº Processo",
                          width: "100px",
                          data: (p: any) => <Text size="md">{p.cvfRef}</Text>,
                        },
                        {
                          label: "Nome do documento",
                          data: (p: any) => <Text size="md">{p.name}</Text>,
                        },
                        {
                          label: "",
                          data: (p: any) => (
                            <IconButton
                              isLoading={loadingFile}
                              onClick={() => download(p)}
                              aria-label="Download"
                              variant="ghost"
                              color={crm.brand}
                              _hover={{ bg: crm.brandSoft }}
                              icon={<FontAwesomeIcon icon={faDownload} />}
                            />
                          ),
                        },
                      ]
                }
                page={filteredPage}
                setPage={setFilteredPage}
                {...{
                  filter,
                  setFilter,
                  tags,
                  get,
                  pageIndex,
                  setPageIndex,
                  pageSize,
                  setPageSize,
                }}
                emptyMessage="Não há documentos ainda"
              />
            </Box>
          </Flex>
        </Flex>
      </Layout>
    </Box>
  );
};

export default Documents;
